import { effect, Injectable, untracked, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { OriginalQueryParamsSerializerService } from './original-query-params.serializer.service';
import { QueryParamsDto } from './model/sidebar-query-params.model';
import { QueryParamsToStateService } from './query-params-to-state.service';
import { SidebarState } from '../state/sidebar.state';
import { RestoreSidebarStateService } from '../state/restore-sidebar-state.service';
import { createStateObject } from '../state/utils/sidebar-state.utils';

@Injectable({
  providedIn: 'root',
})
export class SidebarQueryParamsService {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly originalQueryParamsSerializerService = inject(
    OriginalQueryParamsSerializerService
  );
  private readonly queryParamsToStateService = inject(
    QueryParamsToStateService
  );
  private readonly sidebarState = inject(SidebarState);
  private readonly restoreSidebarStateService = inject(
    RestoreSidebarStateService
  );

  syncQueryParamsWithSidebarState(): void {
    effect(() => {
      const wholeState = this.sidebarState.wholeState();
      untracked(() => {
        this.putStateIntoQueryParameters(
          this.queryParamsToStateService.convertStateToQueryParams(wholeState)
        );
      });
    });
  }

  applyCurrentQueryParams(): void {
    const currentBrowserQueryParams = this.getCurrentQueryParams();
    const sidebarQueryParams =
      this.originalQueryParamsSerializerService.deserializeQueryParamsIfAny(
        currentBrowserQueryParams
      );
    if (sidebarQueryParams === null) {
      return;
    }

    // 1) Convert sidebar query params to state
    const sidebarState =
      this.queryParamsToStateService.convertQueryParamsToState(
        sidebarQueryParams
      );
    // 2) Update state
    this.sidebarState.initializeStateFromQueryParams(
      createStateObject(sidebarState)
    );
    // 3) Run restoring from state
    this.restoreSidebarStateService.restoreState(sidebarState);
  }

  private getCurrentQueryParams(): Params {
    return this.activatedRoute.snapshot.queryParams;
  }

  private updateQueryParams(params: Params): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      replaceUrl: true,
      queryParams: params,
      queryParamsHandling: 'merge',
    });
  }

  private putStateIntoQueryParameters(
    sidebarQueryParams: QueryParamsDto
  ): void {
    const params =
      this.originalQueryParamsSerializerService.serializeQueryParams(
        sidebarQueryParams
      );
    this.updateQueryParams(params);
  }
}
